import { Link } from 'react-router';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { avatarStyle, initialsOf } from '@/features/crm/lib/avatar';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { MultiSelect } from '@/features/crm/components/MultiSelect';
import {
  DisclosureCell,
  DisclosureHeaderCell,
  detailRowId,
  totalColumns,
  useExpandedRows,
  zebraFor,
} from '@/features/crm/components/ExpandableRow';
import {
  DIMENSION_LABEL,
  type GroupDimension,
  groupCustomers,
} from '@/features/crm/lib/book-grouping';
import {
  accountCodesCell,
  customerMetaLine,
} from '@/features/crm/lib/account-codes';

interface Customer {
  customer_id: string;
  name: string;
  /**
   * The ONE account number, or null when the customer has none or has several.
   * `accountnumber` upstream is a comma-joined list of booking/GDS codes — the
   * API resolves it (lib/account-codes.ts there); these three fields are what
   * comes back.
   */
  account_number: string | null;
  account_codes?: string[];
  additional_account_codes?: string[];
  domain: string | null;
  status: string | null;
  cust_ae: string | null;
  cust_csm: string | null;
  cust_ops_mgr: string | null;
  industry: string | null;
  size: string | null;
  city: string | null;
  state: string | null;
  total_spend_12m: number | null;
  travelers_12m: number | null;
  btp_enabled_hint: boolean | null;
  health_band: string | null;
  health_score: number | null;
  /** Book facts, used by the grouped rollup. See book-grouping.ts. */
  air_pnrs_90?: number | null;
  hotel_pnrs_90?: number | null;
  car_pnrs_90?: number | null;
  online_spend_12m?: number | null;
  full_service_spend_12m?: number | null;
  key_contact: string | null;
  key_contact_email: string | null;
  customer_start_date: string | null;
  is_global: boolean | null;
  has_partner_agency: boolean | null;
}

interface SearchResult {
  customers: Customer[];
  total: number;
  query: string;
}

type Filters = {
  aes: string[]; // multi — book of business across teammates
  csms: string[]; // multi
  opsMgrs: string[]; // multi
  industry: string;
  size: string;
  status: string;
  btp: string;
  health: string;
  tenure: string; // '' | lt1 | 1to5 | 5to10 | gt10
  global: string; // '' | yes | no
  partnerAgency: string; // '' | yes | no  (has a partner/host agency relationship)
  underPenetrated: string; // '' | yes  (large size band, spend below band median)
  /**
   * '' | yes — customers with NO AE and NO CSM.
   *
   * You could always filter TO an advisor, never to nobody, so finding unowned
   * accounts was impossible in the UI. On the live book that is 203 of 777 active
   * customers carrying $92M — most of them Andavo house/leisure accounts that
   * legitimately have no corporate advisor, but 38 corporate (1001*) accounts
   * worth $11M that look like real gaps.
   */
  unassigned: string;
};

const EMPTY_FILTERS: Filters = {
  aes: [],
  csms: [],
  opsMgrs: [],
  industry: '',
  size: '',
  status: '',
  btp: '',
  health: '',
  tenure: '',
  global: '',
  partnerAgency: '',
  underPenetrated: '',
  unassigned: '',
};

function countActive(f: Filters): number {
  let n = 0;
  if (f.aes.length) n++;
  if (f.csms.length) n++;
  if (f.opsMgrs.length) n++;
  if (f.industry) n++;
  if (f.size) n++;
  if (f.status) n++;
  if (f.btp) n++;
  if (f.health) n++;
  if (f.tenure) n++;
  if (f.global) n++;
  if (f.partnerAgency) n++;
  if (f.underPenetrated) n++;
  return n;
}

// "Big company" size bands — used for the size-vs-spend (under-penetration) flag.
const LARGE_BANDS = new Set(['Enterprise', 'Large Market']);

// Customer tenure bucket from start date (seeded). Date.now is fine in app code.
function tenureBucketOf(startDate: string | null): string | null {
  if (!startDate) return null;
  const t = new Date(startDate).getTime();
  if (isNaN(t)) return null;
  const years = (Date.now() - t) / (365.25 * 24 * 3600 * 1000);
  if (years < 1) return 'lt1';
  if (years < 5) return '1to5';
  if (years < 10) return '5to10';
  return 'gt10';
}

export default function CustomersPage() {
  const { me } = useMe();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState<
    'name' | 'total_spend_12m' | 'travelers_12m' | 'health_score'
  >('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [groupBy, setGroupBy] = useState<GroupDimension>('none');
  const groupRows = useExpandedRows();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = debouncedQuery
          ? `/api/customers?q=${encodeURIComponent(debouncedQuery)}&limit=5000`
          : '/api/customers?limit=5000';
        const data = await apiFetch<SearchResult>(url);
        if (!cancelled) setResults(data.customers);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const { aes, csms, opsMgrs, industries, sizes, statuses } = useMemo(() => {
    const aes = new Set<string>();
    const csms = new Set<string>();
    const ops = new Set<string>();
    const industries = new Set<string>();
    const sizes = new Set<string>();
    const statuses = new Set<string>();
    for (const c of results) {
      if (c.cust_ae) aes.add(c.cust_ae);
      if (c.cust_csm) csms.add(c.cust_csm);
      if (c.cust_ops_mgr) ops.add(c.cust_ops_mgr);
      if (c.industry) industries.add(c.industry);
      if (c.size) sizes.add(c.size);
      if (c.status) statuses.add(c.status);
    }
    return {
      aes: Array.from(aes).sort(),
      csms: Array.from(csms).sort(),
      opsMgrs: Array.from(ops).sort(),
      industries: Array.from(industries).sort(),
      sizes: [
        'Micro',
        'Small',
        'Mid Market',
        'Large Market',
        'Enterprise',
      ].filter((s) => sizes.has(s)),
      statuses: Array.from(statuses).sort(),
    };
  }, [results]);

  const filtered = useMemo(() => {
    let rows = results;
    if (filters.aes.length)
      rows = rows.filter((c) => c.cust_ae && filters.aes.includes(c.cust_ae));
    if (filters.csms.length)
      rows = rows.filter(
        (c) => c.cust_csm && filters.csms.includes(c.cust_csm),
      );
    if (filters.unassigned === 'yes') {
      rows = rows.filter((c) => !c.cust_ae?.trim() && !c.cust_csm?.trim());
    }
    if (filters.opsMgrs.length)
      rows = rows.filter(
        (c) => c.cust_ops_mgr && filters.opsMgrs.includes(c.cust_ops_mgr),
      );
    if (filters.industry)
      rows = rows.filter((c) => c.industry === filters.industry);
    if (filters.size) rows = rows.filter((c) => c.size === filters.size);
    if (filters.status) rows = rows.filter((c) => c.status === filters.status);
    if (filters.btp === 'yes')
      rows = rows.filter((c) => c.btp_enabled_hint === true);
    if (filters.btp === 'no') rows = rows.filter((c) => !c.btp_enabled_hint);
    if (filters.health)
      rows = rows.filter((c) => c.health_band === filters.health);
    if (filters.tenure)
      rows = rows.filter(
        (c) => tenureBucketOf(c.customer_start_date) === filters.tenure,
      );
    if (filters.global === 'yes')
      rows = rows.filter((c) => c.is_global === true);
    if (filters.global === 'no')
      rows = rows.filter((c) => c.is_global !== true);
    if (filters.partnerAgency === 'yes')
      rows = rows.filter((c) => c.has_partner_agency === true);
    if (filters.partnerAgency === 'no')
      rows = rows.filter((c) => c.has_partner_agency !== true);
    if (filters.underPenetrated === 'yes') {
      // "Enterprise spending light": large size band, 12-mo spend below the
      // median for that band (spending less than peers of the same size).
      const byBand = new Map<string, number[]>();
      for (const c of results) {
        if (c.size && LARGE_BANDS.has(c.size)) {
          const arr = byBand.get(c.size) || [];
          arr.push(c.total_spend_12m ?? 0);
          byBand.set(c.size, arr);
        }
      }
      const med = new Map<string, number>();
      for (const [band, arr] of byBand) {
        arr.sort((a, b) => a - b);
        med.set(band, arr[Math.floor(arr.length / 2)] ?? 0);
      }
      rows = rows.filter(
        (c) =>
          !!c.size &&
          LARGE_BANDS.has(c.size) &&
          (c.total_spend_12m ?? 0) < (med.get(c.size) ?? 0),
      );
    }
    rows = [...rows].sort((a, b) => {
      const av = (a[sortKey] ?? '') as string | number;
      const bv = (b[sortKey] ?? '') as string | number;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return rows;
  }, [results, filters, sortKey, sortDir]);

  const totalSpend = useMemo(
    () => filtered.reduce((s, c) => s + (c.total_spend_12m ?? 0), 0),
    [filtered],
  );
  const totalTravelers = useMemo(
    () => filtered.reduce((s, c) => s + (c.travelers_12m ?? 0), 0),
    [filtered],
  );

  const setSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const activeFilterCount = countActive(filters);
  const hasActiveQuery = Boolean(debouncedQuery);

  /**
   * Grouped books, built from the ALREADY filtered+sorted rows — so grouping
   * composes with the filters and the column sort rather than fighting them: the
   * sort orders customers inside each book, the groups order themselves by spend.
   */
  const grouping = groupBy !== 'none';
  const groups = useMemo(
    () => (grouping ? groupCustomers(filtered, groupBy) : []),
    [grouping, filtered, groupBy],
  );

  /**
   * Collapse open books when the underlying row SET changes.
   *
   * The bug: `collapseAll()` ran only in the Group-by `<select>`, but the fetch
   * effect and the `filtered`/`groups` memos also recompute when a filter chip,
   * the debounced search, or a replacement fetch changes the rows — and expansion
   * survived. So a book whose key exists in BOTH result sets rendered already
   * open, showing a different set of customers than the one the reader actually
   * opened. Silent, and it contradicts the shared `useExpandedRows` contract that
   * expansion collapses when the underlying rows change.
   *
   * Keyed on MEMBERSHIP, not on `filtered`'s identity. `filtered` is also a new
   * array after a column sort, and a sort does not change which customers are in
   * a book — only their order inside it. Depending on the array would slam every
   * open book shut on a header click, which is a regression nobody asked for.
   * Sorting the ids makes the signature order-insensitive, so only a genuine
   * add/remove fires this.
   */
  const membershipKey = useMemo(
    () =>
      filtered
        .map((c) => c.customer_id)
        .sort()
        .join(','),
    [filtered],
  );
  useEffect(() => {
    groupRows.collapseAll();
    // `collapseAll` is a stable useCallback([]); listing it keeps the lint honest
    // without re-running this on every render.
  }, [membershipKey, groupRows.collapseAll]);
  // Data columns in the table below. `+ 1` for the disclosure column is handled
  // by totalColumns() where it is needed; this is the count the empty/loading
  // rows must span.
  const dataColumns = 8;
  // totalColumns() owns the `+ 1` for the disclosure column — inlining it here is
  // how the two drift and shear the table's right edge when a row is open.
  const spanAll = grouping ? totalColumns(dataColumns) : dataColumns;

  // "Show my book" — auto-fills AE OR CSM filter with the signed-in user.
  // Works when the user's display name matches what's in Customer Master
  // (which it does for our seeded users; in prod the email match is the
  // safer pivot, but Customer Master only has names, not emails).
  const userName = me?.name || '';
  const myAeCount = userName
    ? results.filter((c) => c.cust_ae === userName).length
    : 0;
  const myCsmCount = userName
    ? results.filter((c) => c.cust_csm === userName).length
    : 0;
  const myBookCount = myAeCount + myCsmCount;
  const myFilterActive =
    myAeCount > 0
      ? filters.aes.includes(userName) && filters.aes.length === 1
      : filters.csms.includes(userName) && filters.csms.length === 1;
  const setMyBook = () => {
    if (!userName) return;
    if (myFilterActive) {
      setFilters({ ...EMPTY_FILTERS });
    } else if (myAeCount > 0) {
      setFilters({ ...EMPTY_FILTERS, aes: [userName] });
    } else {
      setFilters({ ...EMPTY_FILTERS, csms: [userName] });
    }
  };

  const handleExport = () => {
    const cols = [
      'Customer',
      'Customer ID',
      'Account #',
      'Account Codes',
      'Status',
      'AE',
      'CSM',
      'Industry',
      'Size',
      '12m Spend',
      'Travelers',
      'Hotel Supplier',
      'Health',
    ];
    const lines = [cols.join(',')];
    for (const c of filtered) {
      const row = [
        csv(c.name),
        c.customer_id,
        csv(c.account_number ?? ''),
        csv(accountCodesCell(c.account_codes)),
        c.status || '',
        csv(c.cust_ae || ''),
        csv(c.cust_csm || ''),
        csv(c.industry || ''),
        c.size || '',
        c.total_spend_12m || 0,
        c.travelers_12m || 0,
        c.btp_enabled_hint ? 'Yes' : 'No',
        c.health_band || '',
      ];
      lines.push(row.join(','));
    }
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Customers</h1>
        <p className="page-intro">
          Search and open customer profiles. Notes, settings, follow-ups and
          Hotel Supplier Management records live in Customer Management; trip
          and spend data is pulled live from Domo. Filter, sort, and export the
          slice you care about.
        </p>
      </div>

      <div className="kpi-row kpi-row-4">
        <div className="kpi-card kpi-highlight">
          <span className="kpi-label">Customers</span>
          <span className="kpi-value">{filtered.length.toLocaleString()}</span>
          <span className="kpi-sub">
            {hasActiveQuery || activeFilterCount > 0
              ? `from ${results.length.toLocaleString()} loaded`
              : 'in view'}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Total Spend · 12mo</span>
          <span className="kpi-value">{fmtMoneyShort(totalSpend)}</span>
          <span className="kpi-sub">across this slice</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Travelers · 12mo</span>
          <span className="kpi-value">{totalTravelers.toLocaleString()}</span>
          <span className="kpi-sub">unique in 12 months</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Account Execs</span>
          <span className="kpi-value">{aes.length}</span>
          <span className="kpi-sub">covering this view</span>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <span className="section-label">Customer directory</span>
          <span className="section-meta">
            {loading
              ? 'Refreshing…'
              : `${filtered.length.toLocaleString()} of ${results.length.toLocaleString()}`}
          </span>
        </div>
        <div className="section-body">
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              marginBottom: 14,
            }}
          >
            <div style={{ flex: '1 1 320px', maxWidth: 520 }}>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, account #, domain, contact, AE or CSM…"
                className="search-input"
                autoFocus
              />
            </div>
            {/* GROUPING. The list could always FILTER to one advisor but never
                GROUP by one, so comparing two books meant running the filter
                twice and writing the numbers down. Grouping composes with the
                filters rather than replacing them: filter to a team, then group
                by CSM to see the split inside it. */}
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
              htmlFor="group-by"
            >
              <span className="field-label-inline">Group by</span>
              <select
                className="filter-select"
                data-testid="group-by"
                id="group-by"
                onChange={(e) => {
                  setGroupBy(e.target.value as GroupDimension);
                  // Collapse on regroup: the open-row ids belong to the previous
                  // grouping, and a stale id would open whichever group happened
                  // to reuse the key.
                  groupRows.collapseAll();
                }}
                value={groupBy}
              >
                {(['none', 'ae', 'csm', 'ops'] as GroupDimension[]).map((d) => (
                  <option key={d} value={d}>
                    {DIMENSION_LABEL[d]}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="btn btn-outline"
              onClick={handleExport}
              disabled={filtered.length === 0}
              title="Download visible rows as CSV"
            >
              Export CSV
            </button>
          </div>

          {/* Filter chips — AE / CSM / Ops Mgr are multi-select so a single
              user can build a book-of-business view across themselves + a
              partner, and a manager can pull the union across their team. */}
          <div className="filter-bar">
            <span className="field-label-inline">Filter by</span>
            {myBookCount > 0 && (
              <button
                type="button"
                onClick={setMyBook}
                title={`Show only customers where you are AE or CSM (${myBookCount} accounts)`}
                style={{
                  padding: '6px 12px',
                  border: `1px solid ${myFilterActive ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--fs-base)',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                  background: myFilterActive
                    ? 'var(--accent)'
                    : 'var(--bg-card)',
                  color: myFilterActive ? '#fff' : 'var(--accent-darker)',
                  cursor: 'pointer',
                  boxShadow: 'var(--shadow-1)',
                }}
              >
                {myFilterActive ? '✓ My book' : `My book (${myBookCount})`}
              </button>
            )}
            {/* The AE / CSM selects are back for EVERYONE, because they are
                multi-select: a CSM ticking themselves plus a partner is widening
                their own view (co-covered accounts), not viewing someone else's
                book instead of theirs. Taylor's rule was about the latter, and
                removing these entirely took away a deliberate existing behaviour.
                Manager level is still what "My book" vs another advisor's book
                turns on, on CRM Home's pivot. */}
            {/* The counterpart to "My book": find accounts nobody owns. Sits next
                to it because they are the same question from opposite ends —
                "mine" and "nobody's". */}
            <button
              type="button"
              onClick={() =>
                setFilters({
                  ...EMPTY_FILTERS,
                  unassigned: filters.unassigned === 'yes' ? '' : 'yes',
                })
              }
              title="Customers with no AE and no CSM"
              style={{
                padding: '6px 12px',
                border: `1px solid ${filters.unassigned === 'yes' ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--fs-base)',
                fontFamily: 'inherit',
                fontWeight: 600,
                background:
                  filters.unassigned === 'yes'
                    ? 'var(--accent)'
                    : 'var(--bg-card)',
                color:
                  filters.unassigned === 'yes'
                    ? '#fff'
                    : 'var(--accent-darker)',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-1)',
              }}
            >
              {filters.unassigned === 'yes' ? '✓ Unassigned' : 'Unassigned'}
            </button>
            <MultiSelect
              value={filters.aes}
              onChange={(v) => setFilters({ ...filters, aes: v })}
              placeholder="Account Executive"
              options={aes}
              minWidth={200}
            />
            <MultiSelect
              value={filters.csms}
              onChange={(v) => setFilters({ ...filters, csms: v })}
              placeholder="CSM"
              options={csms}
              minWidth={160}
            />
            {opsMgrs.length > 0 && (
              <MultiSelect
                value={filters.opsMgrs}
                onChange={(v) => setFilters({ ...filters, opsMgrs: v })}
                placeholder="Ops Manager"
                options={opsMgrs}
                minWidth={180}
              />
            )}
            <Select
              value={filters.industry}
              onChange={(v) => setFilters({ ...filters, industry: v })}
              placeholder="Industry"
              options={industries}
            />
            <Select
              value={filters.size}
              onChange={(v) => setFilters({ ...filters, size: v })}
              placeholder="Size"
              options={sizes}
            />
            <Select
              value={filters.status}
              onChange={(v) => setFilters({ ...filters, status: v })}
              placeholder="Status"
              options={statuses}
            />
            <Select
              value={filters.btp}
              onChange={(v) => setFilters({ ...filters, btp: v })}
              placeholder="Hotel Supplier"
              options={[
                { value: 'yes', label: 'Supplier-enrolled' },
                { value: 'no', label: 'Not enrolled' },
              ]}
            />
            <Select
              value={filters.health}
              onChange={(v) => setFilters({ ...filters, health: v })}
              placeholder="Health"
              options={[
                { value: 'Growing', label: 'Growing' },
                { value: 'Stable', label: 'Stable' },
                { value: 'At Risk', label: 'At Risk' },
                { value: 'Critical', label: 'Critical' },
              ]}
            />
            <Select
              value={filters.tenure}
              onChange={(v) => setFilters({ ...filters, tenure: v })}
              placeholder="Tenure"
              options={[
                { value: 'lt1', label: 'Under 1 year' },
                { value: '1to5', label: '1–5 years' },
                { value: '5to10', label: '5–10 years' },
                { value: 'gt10', label: 'Over 10 years' },
              ]}
            />
            <Select
              value={filters.global}
              onChange={(v) => setFilters({ ...filters, global: v })}
              placeholder="Global"
              options={[
                { value: 'yes', label: 'Global' },
                { value: 'no', label: 'Domestic' },
              ]}
            />
            <Select
              value={filters.partnerAgency}
              onChange={(v) => setFilters({ ...filters, partnerAgency: v })}
              placeholder="Partner agency"
              options={[
                { value: 'yes', label: 'Has partner agency' },
                { value: 'no', label: 'No partner agency' },
              ]}
            />
            <Select
              value={filters.underPenetrated}
              onChange={(v) => setFilters({ ...filters, underPenetrated: v })}
              placeholder="Size vs spend"
              options={[{ value: 'yes', label: 'Large + spending light' }]}
            />
            {activeFilterCount > 0 && (
              <button
                className="filter-clear"
                onClick={() => setFilters(EMPTY_FILTERS)}
              >
                Clear {activeFilterCount}
              </button>
            )}
          </div>

          {/* Active-selection chip row — shows exactly who's in scope so the
              user knows what their "book of business" view contains. */}
          {(filters.aes.length > 0 ||
            filters.csms.length > 0 ||
            filters.opsMgrs.length > 0) && (
            <div
              style={{
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                alignItems: 'center',
                marginTop: 10,
                marginBottom: 6,
                fontSize: 'var(--fs-sm)',
              }}
            >
              <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
                Book in view:
              </span>
              {filters.aes.map((name) => (
                <SelectionPill
                  key={`ae-${name}`}
                  label={name}
                  role="AE"
                  onRemove={() =>
                    setFilters({
                      ...filters,
                      aes: filters.aes.filter((n) => n !== name),
                    })
                  }
                />
              ))}
              {filters.csms.map((name) => (
                <SelectionPill
                  key={`csm-${name}`}
                  label={name}
                  role="CSM"
                  onRemove={() =>
                    setFilters({
                      ...filters,
                      csms: filters.csms.filter((n) => n !== name),
                    })
                  }
                />
              ))}
              {filters.opsMgrs.map((name) => (
                <SelectionPill
                  key={`ops-${name}`}
                  label={name}
                  role="Ops"
                  onRemove={() =>
                    setFilters({
                      ...filters,
                      opsMgrs: filters.opsMgrs.filter((n) => n !== name),
                    })
                  }
                />
              ))}
            </div>
          )}

          {error && <div className="banner banner-error">{error}</div>}

          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  {grouping && <DisclosureHeaderCell />}
                  <Th
                    label="Customer"
                    sortKey="name"
                    sortKeyActive={sortKey}
                    sortDir={sortDir}
                    onClick={() => setSort('name')}
                  />
                  <th>Status</th>
                  <th>Account Team</th>
                  <th>Industry / Size</th>
                  <Th
                    label="12m Spend"
                    sortKey="total_spend_12m"
                    sortKeyActive={sortKey}
                    sortDir={sortDir}
                    onClick={() => setSort('total_spend_12m')}
                    right
                  />
                  {/* gitleaks:allow — UI column identifier, not a secret */}
                  <Th
                    label="Travelers"
                    sortKey="travelers_12m"
                    sortKeyActive={sortKey}
                    sortDir={sortDir}
                    onClick={() => setSort('travelers_12m')}
                    right
                  />
                  <Th
                    label="Health"
                    sortKey="health_score"
                    sortKeyActive={sortKey}
                    sortDir={sortDir}
                    onClick={() => setSort('health_score')}
                  />
                  <th>Flags</th>
                </tr>
              </thead>
              <tbody>
                {loading && filtered.length === 0 && (
                  <tr className="empty">
                    <td colSpan={spanAll}>Loading customers…</td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr className="empty">
                    <td colSpan={spanAll}>
                      {hasActiveQuery || activeFilterCount > 0
                        ? 'No customers match the current search + filters.'
                        : 'No customers loaded.'}
                    </td>
                  </tr>
                )}
                {/* GROUPED: a header row per book, its customers nested beneath
                    when open. Rendered as sibling <tr>s in one <tbody> rather
                    than a <tbody> per group, so the table keeps one column grid
                    and the rollup figures stay aligned under the columns they
                    total. */}
                {grouping &&
                  groups.map((g, gi) => {
                    const open = groupRows.isExpanded(g.key);
                    const panelId = detailRowId('book', g.key);
                    // ZEBRA FROM THE GROUP INDEX, not :nth-child. Expanding a book
                    // inserts its members as sibling <tr>s, which flips the stripe
                    // of every row below it — the bug ExpandableRow's header warns
                    // about. Members carry their HEADER's band (crm-styles.css rule
                    // 2), so an open book reads as one banded block.
                    const band = zebraFor(gi);
                    return (
                      <Fragment key={g.key}>
                        <tr
                          className="group-row"
                          data-testid={`book-group-${g.key}`}
                          data-zebra={band}
                        >
                          <DisclosureCell
                            controls={panelId}
                            label={`${g.label} — ${g.rollup.accounts} accounts`}
                            onToggle={() => groupRows.toggle(g.key)}
                            open={open}
                            testId={`book-toggle-${g.key}`}
                          />
                          <td colSpan={4}>
                            <span className="group-row-label">{g.label}</span>
                            {g.unassigned && (
                              // Named as a gap, not as a person. On the live book
                              // this is the largest "book" there is, and reading
                              // it as an advisor's would be badly misleading.
                              <span
                                className="badge"
                                style={{ marginLeft: 8 }}
                                title="These customers have nobody in this role"
                              >
                                no {DIMENSION_LABEL[groupBy].toLowerCase()}
                              </span>
                            )}
                            <span
                              style={{
                                marginLeft: 8,
                                color: 'var(--text-muted)',
                                fontSize: 'var(--fs-sm)',
                              }}
                            >
                              {g.rollup.accounts.toLocaleString()}{' '}
                              {g.rollup.accounts === 1 ? 'account' : 'accounts'}
                              {/* What this book CARRIES, on the same line as its
                                  size. Facts about the book, deliberately not a
                                  score — ranking CSMs against each other is the
                                  Domo app's job, not this list's. */}
                              {g.rollup.transactions > 0 ? (
                                <>
                                  {' · '}
                                  {g.rollup.transactions.toLocaleString()}{' '}
                                  transactions
                                </>
                              ) : null}
                              {g.rollup.onlineShare !== null ? (
                                <>
                                  {' · '}
                                  {fmtSharePct(g.rollup.onlineShare)} online
                                </>
                              ) : null}
                            </span>
                          </td>
                          <td className="right">
                            <strong>{fmtMoneyShort(g.rollup.spend)}</strong>
                          </td>
                          <td className="right">
                            {g.rollup.travelers.toLocaleString()}
                          </td>
                          <td>
                            {/* "—" not "0" when nothing in the book is scored:
                                unmeasured is not unhealthy. */}
                            {g.rollup.avgHealth === null
                              ? '—'
                              : `avg ${Math.round(g.rollup.avgHealth)}`}
                          </td>
                          <td>
                            {g.rollup.atRisk > 0 ? (
                              <span className="badge badge-pink">
                                {g.rollup.atRisk} at risk
                              </span>
                            ) : (
                              <span
                                style={{
                                  color: 'var(--text-faint)',
                                  fontSize: 'var(--fs-xs)',
                                }}
                              >
                                —
                              </span>
                            )}
                          </td>
                        </tr>
                        {open &&
                          g.customers.map((c, ci) => (
                            <CustomerRow
                              customer={c}
                              // aria-controls on the disclosure button points HERE.
                              // The revealed content is a run of sibling <tr>s
                              // rather than one <DetailRow>, so without an id on
                              // the first member the control resolved to nothing —
                              // aria-expanded toggled but the reference dangled.
                              id={ci === 0 ? panelId : undefined}
                              indented
                              key={c.customer_id}
                              zebra={band}
                            />
                          ))}
                      </Fragment>
                    );
                  })}
                {!grouping &&
                  filtered.map((c) => (
                    <CustomerRow customer={c} key={c.customer_id} />
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── rows ─────────────────────────────────────────────────────────────

/**
 * One customer row.
 *
 * Extracted from the table body when grouping landed, so the flat list and the
 * rows nested under a group header are literally the SAME component. Two copies
 * would drift — and the way they drift is that a column gets added to one, which
 * shears the shared column grid only in the other view.
 *
 * `indented` renders the leading spacer that keeps a nested row aligned under the
 * group's disclosure column. It is a real (empty) cell rather than padding,
 * because the table's columns have to line up with the rollup figures in the
 * header above it.
 */
function CustomerRow({
  customer: c,
  id,
  indented,
  zebra,
}: {
  customer: Customer;
  /** Set on the FIRST member of an open group, so the group's `aria-controls` resolves. */
  id?: string;
  indented?: boolean;
  /**
   * Stripe band, inherited from the group header so an open book reads as one
   * block. Omitted in the flat view, which keeps its existing :nth-child
   * striping — nothing is inserted there, so there is no parity to break.
   */
  zebra?: 'even' | 'odd';
}) {
  return (
    <tr data-zebra={zebra} id={id}>
      {indented && <td className="col-disclosure" />}
      <td>
        <Link
          to={`/crm/customers/${encodeURIComponent(c.customer_id)}`}
          className="cust-cell"
          style={{ textDecoration: 'none' }}
        >
          <span style={avatarStyle(c.name, 36)}>{initialsOf(c.name)}</span>
          <span className="cust-cell-body">
            <span className="cust-cell-name">{c.name}</span>
            <span className="cust-cell-meta" data-testid="cust-cell-meta">
              {customerMetaLine(c.customer_id, c.additional_account_codes)}
            </span>
          </span>
        </Link>
      </td>
      <td>
        <StatusDot status={c.status} />
      </td>
      <td>
        <div style={{ fontSize: 'var(--fs-base)' }}>{c.cust_ae || '—'}</div>
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
          {c.cust_csm || '—'}
        </div>
      </td>
      <td>
        <div style={{ fontSize: 'var(--fs-base)' }}>{c.industry || '—'}</div>
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
          {c.size || '—'}
        </div>
      </td>
      <td className="right">
        {c.total_spend_12m == null ? '—' : fmtMoneyShort(c.total_spend_12m)}
      </td>
      <td className="right">
        {c.travelers_12m == null ? '—' : c.travelers_12m.toLocaleString()}
      </td>
      <td>
        <HealthPill band={c.health_band} score={c.health_score} />
      </td>
      <td>
        {c.btp_enabled_hint ? (
          <span className="badge badge-pink">Hotel Supplier</span>
        ) : (
          <span
            style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-xs)' }}
          >
            —
          </span>
        )}
      </td>
    </tr>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────

function Th({
  label,
  sortKey,
  sortKeyActive,
  sortDir,
  onClick,
  right,
}: {
  label: string;
  sortKey: string;
  sortKeyActive: string;
  sortDir: 'asc' | 'desc';
  onClick: () => void;
  right?: boolean;
}) {
  const active = sortKey === sortKeyActive;
  return (
    <th
      onClick={onClick}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      className={right ? 'right' : ''}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        <span
          style={{
            fontSize: 'var(--fs-xs)',
            opacity: active ? 1 : 0.25,
            transition: 'opacity 140ms',
          }}
        >
          {active && sortDir === 'desc' ? '▼' : '▲'}
        </span>
      </span>
    </th>
  );
}

function StatusDot({ status }: { status: string | null }) {
  const map: Record<string, string> = {
    Active: 'status-dot status-active',
    Inactive: 'status-dot status-inactive',
  };
  const cls = (status && map[status]) || 'status-dot status-unknown';
  return <span className={cls}>{status || 'Unknown'}</span>;
}

/**
 * A share as a whole percent, floored at "<1%".
 *
 * `Math.round` alone renders a real 0.4% share as "0% online", which reads as
 * "this book books nothing online" — the exact misread this section exists to
 * avoid. A book with genuinely no online spend has `onlineShare === null` and the
 * segment is not rendered at all, so "<1%" can only ever mean "some, but under a
 * point". 100% is not floored from above for the same reason: 99.6% rounding to
 * "100% online" is a fair reading of the book, while "nothing" is not.
 */
function fmtSharePct(share: number): string {
  const pct = share * 100;
  if (pct > 0 && pct < 1) return '<1%';
  return `${Math.round(pct)}%`;
}

function HealthPill({
  band,
  score,
}: {
  band: string | null;
  score: number | null;
}) {
  if (!band) return <span style={{ color: 'var(--text-faint)' }}>—</span>;
  const slug = band.toLowerCase().replace(/\s+/g, '_');
  return (
    <span className={`health-pill ${slug}`}>
      {band}
      {score != null && (
        <span style={{ fontWeight: 600, marginLeft: 4, opacity: 0.8 }}>
          · {score}
        </span>
      )}
    </span>
  );
}

type SelectOption = string | { value: string; label: string };

function Select({
  value,
  onChange,
  placeholder,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: SelectOption[];
}) {
  return (
    <select
      value={value}
      data-active={value ? '1' : '0'}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const label = typeof o === 'string' ? o : o.label;
        return (
          <option key={val} value={val}>
            {label}
          </option>
        );
      })}
    </select>
  );
}

/**
 * SelectionPill — chip showing one person in the active book-of-business
 * view with role badge (AE/CSM/Ops) and a click-to-remove ✕. Renders below
 * the filter bar when any AE/CSM/Ops filter is set so the user always knows
 * whose accounts are in scope.
 */
function SelectionPill({
  label,
  role,
  onRemove,
}: {
  label: string;
  role: 'AE' | 'CSM' | 'Ops';
  onRemove: () => void;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 4px 3px 8px',
        background: 'var(--accent-light)',
        color: 'var(--accent-darker)',
        border: '1px solid var(--accent)',
        borderRadius: 999,
        fontSize: 'var(--fs-sm)',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 700,
          color: 'var(--accent)',
          letterSpacing: 0.3,
          marginRight: 2,
        }}
      >
        {role}
      </span>
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${role} ${label}`}
        title="Remove from book view"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--accent-darker)',
          cursor: 'pointer',
          padding: '0 4px',
          fontSize: 'var(--fs-base)',
          lineHeight: 1,
          fontWeight: 700,
          fontFamily: 'inherit',
        }}
      >
        ×
      </button>
    </span>
  );
}

function csv(v: string | null | undefined): string {
  const s = String(v ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function fmtMoneyShort(v: number): string {
  if (!isFinite(v) || v === 0) return '—';
  if (v >= 1_000_000_000) return '$' + (v / 1_000_000_000).toFixed(2) + 'B';
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  if (v >= 10_000) return '$' + (v / 1_000).toFixed(0) + 'k';
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
